// src/components/DrBullsheetAICard.jsx
import React, { useState, useRef, useEffect } from 'react';
import Card from '@mui/material/Card';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDInput from 'components/MDInput';
import MDButton from 'components/MDButton';
import Divider from '@mui/material/Divider';
import Avatar from '@mui/material/Avatar';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import InfoIcon from '@mui/icons-material/Info';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import SendIcon from '@mui/icons-material/Send';
import HistoryIcon from '@mui/icons-material/History';
import DeleteIcon from '@mui/icons-material/Delete';
import UpgradeIcon from '@mui/icons-material/Upgrade';
import { collection, getDocs, doc, setDoc, getDoc, increment, serverTimestamp } from 'firebase/firestore';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import axios from 'axios';
import { db } from '../firebaseConfig';

const GEMINI_API_KEY = process.env.REACT_APP_GEMINI_API_KEY;
const GEMINI_URL = `https://generativelanguage.googleapis.com/v1/models/gemini-2.5-flash-lite:generateContent?key=${GEMINI_API_KEY}`;
const TWELVEDATA_BASE = process.env.REACT_APP_BASE_URL;
const TWELVEDATA_KEY = process.env.REACT_APP_API_KEY;

const DAILY_LIMIT = 30;

const DrBullsheetAICard = () => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [chatHistory, setChatHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [currentChatId, setCurrentChatId] = useState(null);
  const [user, setUser] = useState(null);
  const [dailyCount, setDailyCount] = useState(0);
  const [limitReached, setLimitReached] = useState(false);
  const messagesEndRef = useRef(null);
  const auth = getAuth();

  // ——— AUTH LISTENER ———
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (u) => {
      setUser(u);
      if (u) {
        loadAllData(u.uid);
      } else {
        setMessages([]);
        setChatHistory([]);
        setDailyCount(0);
        setLimitReached(false);
      }
    });
    return unsubscribe;
  }, []);

  // ——— LOAD CHATS + DAILY COUNT ———
  const loadAllData = async (uid) => {
    await Promise.all([
      loadChatsFromFirebase(uid),
      loadDailyUsage(uid)
    ]);
  };

  const loadDailyUsage = async (uid) => {
    const today = new Date().toISOString().slice(0, 10); // YYYY-MM-DD
    const usageRef = doc(db, 'Users', uid, 'drBullsheetMeta', 'dailyUsage');
    const snap = await getDoc(usageRef);

    if (snap.exists()) {
      const data = snap.data();
      if (data.date === today) {
        const count = data.count || 0;
        setDailyCount(count);
        setLimitReached(count >= DAILY_LIMIT);
      } else {
        // Old day → reset
        await setDoc(usageRef, { date: today, count: 0 }, { merge: true });
        setDailyCount(0);
        setLimitReached(false);
      }
    } else {
      await setDoc(usageRef, { date: today, count: 0 });
      setDailyCount(0);
      setLimitReached(false);
    }
  };

  const incrementDailyUsage = async () => {
    if (!user) return;
    const usageRef = doc(db, 'Users', user.uid, 'drBullsheetMeta', 'dailyUsage');
    await setDoc(usageRef, { count: increment(1), lastUsed: serverTimestamp() }, { merge: true });
    setDailyCount(prev => {
      const newCount = prev + 1;
      if (newCount >= DAILY_LIMIT) setLimitReached(true);
      return newCount;
    });
  };

  // ——— LOAD CHATS ———
  const loadChatsFromFirebase = async (uid) => {
    const chatsRef = collection(db, 'Users', uid, 'drBullsheetChats');
    const snapshot = await getDocs(chatsRef);
    const firebaseChats = snapshot.docs
      .map(d => ({ id: parseInt(d.id), ...d.data() }))
      .filter(c => !c.deleted)
      .sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));

    if (firebaseChats.length > 0) {
      setChatHistory(firebaseChats);
      const latest = firebaseChats[0];
      setMessages(latest.messages || []);
      setCurrentChatId(latest.id);
    }
  };

  // ——— REST OF YOUR ORIGINAL CODE (unchanged except small tweaks) ———
  useEffect(() => {
    const local = localStorage.getItem('drBullsheetChats');
    if (local && !user) {
      const localChats = JSON.parse(local);
      setChatHistory(localChats);
      if (localChats.length > 0) {
        const latest = localChats[0];
        setMessages(latest.messages || []);
        setCurrentChatId(latest.id);
      }
    }
  }, [user]);

  useEffect(() => {
    if (messages.length > 0 && currentChatId && user) {
      const updated = chatHistory.map(chat =>
        chat.id === currentChatId ? { ...chat, messages, timestamp: new Date() } : chat
      );
      setChatHistory(updated);
      localStorage.setItem('drBullsheetChats', JSON.stringify(updated));
      saveChatToFirebase();
    }
  }, [messages, currentChatId]);

  useEffect(() => {
    if (messages.length > 0 && currentChatId) {
      const firstUserMsg = messages.find(m => m.sender === 'user')?.text || 'New Chat';
      const title = firstUserMsg.length > 30 ? firstUserMsg.substring(0, 27) + '...' : firstUserMsg;
      const updated = chatHistory.map(chat =>
        chat.id === currentChatId ? { ...chat, title } : chat
      );
      setChatHistory(updated);
      localStorage.setItem('drBullsheetChats', JSON.stringify(updated));
    }
  }, [messages, currentChatId]);

  useEffect(() => {
    if (!GEMINI_API_KEY) {
      setMessages([{ text: 'Gemini API key missing in .env. Add REACT_APP_GEMINI_API_KEY=your_key', sender: 'ai' }]);
    }
  }, []);

  const scrollToBottom = () => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  useEffect(scrollToBottom, [messages]);

  const saveChatToFirebase = async () => {
    if (!user || !currentChatId) return;
    const chatRef = doc(db, 'Users', user.uid, 'drBullsheetChats', currentChatId.toString());
    const currentChat = chatHistory.find(c => c.id === currentChatId);
    if (currentChat) {
      await setDoc(chatRef, { ...currentChat, lastUpdated: new Date() }, { merge: true });
    }
  };

  // ——— PARSE MONTH/YEAR ———
  const parseMonthFromPrompt = (prompt) => {
    const monthMap = {
      'january': 0, 'jan': 0, 'february': 1, 'feb': 1, 'march': 2, 'mar': 2,
      'april': 3, 'apr': 3, 'may': 4, 'june': 5, 'jun': 5, 'july': 6, 'jul': 6,
      'august': 7, 'aug': 7, 'september': 8, 'sep': 8, 'sept': 8, 'october': 9, 'oct': 9,
      'november': 10, 'nov': 10, 'december': 11, 'dec': 11
    };
    const lower = prompt.toLowerCase();
    for (const [name, num] of Object.entries(monthMap)) {
      if (lower.includes(name)) return num;
    }
    return null;
  };

  const parseYearFromPrompt = (prompt) => {
    const match = prompt.match(/\b(20\d{2})\b/);
    return match ? parseInt(match[1]) : new Date().getFullYear();
  };

  // ——— FETCH TRADES ———
  const fetchUserTradesInMonth = async (targetMonth, targetYear) => {
    const user = auth.currentUser;
    if (!user) return [];

    const startOfMonth = new Date(targetYear, targetMonth, 1);
    const endOfMonth = new Date(targetYear, targetMonth + 1, 0, 23, 59, 59);

    const tradeAccountsRef = collection(db, 'Users', user.uid, 'tradeAccounts');
    const accountsSnap = await getDocs(tradeAccountsRef);
    const trades = [];

    for (const accDoc of accountsSnap.docs) {
      const tradesRef = collection(db, 'Users', user.uid, 'tradeAccounts', accDoc.id, 'trades');
      const snapshot = await getDocs(tradesRef);

      snapshot.docs.forEach(doc => {
        const data = doc.data();
        let tradeDate;

        if (typeof data.date === 'string') {
          const [y, m, d] = data.date.split('-').map(Number);
          tradeDate = new Date(y, m - 1, d);
        } else if (data.date?.toDate) {
          tradeDate = data.date.toDate();
        } else {
          return;
        }

        if (
          tradeDate >= startOfMonth &&
          tradeDate <= endOfMonth &&
          data.status === 'Closed' &&
          data.returns != null
        ) {
          trades.push({
            id: doc.id,
            symbol: data.symbol,
            direction: data.shortLong,
            entry: parseFloat(data.entry),
            exit: parseFloat(data.exit),
            sl: data.sl ? parseFloat(data.sl) : null,
            returns: parseFloat(data.returns),
            date: tradeDate,
            commission: (() => {
              if (!data.commission || data.commission === 'N/A') return 0;
              const cleaned = data.commission.toString().replace('$', '').trim();
              return parseFloat(cleaned) || 0;
            })(),
          });
        }
      });
    }
    return trades;
  };

  // ——— MARKET CONTEXT ———
  const fetchMarketContext = async (symbol, tradeDate) => {
    try {
      const dateStr = tradeDate.toISOString().split('T')[0];
      const params = { apikey: TWELVEDATA_KEY, symbol, interval: '1day', start_date: dateStr, end_date: dateStr, outputsize: 1 };
      const res = await axios.get(`${TWELVEDATA_BASE}/time_series`, { params });
      const data = res.data.values?.[0];
      if (!data) return null;

      const high = parseFloat(data.high), low = parseFloat(data.low), open = parseFloat(data.open);
      const trueRange = high - low;
      const pipIncrement = calculatePipIncrement(symbol);
      const dailyRangePips = trueRange / pipIncrement;

      return { date: dateStr, high, low, open, dailyRangePips: dailyRangePips.toFixed(1), volatility: ((high - low) / open * 100).toFixed(2) };
    } catch (error) {
      console.error('Market data error:', error);
      return null;
    }
  };

  // ——— PIP INCREMENT ———
  const calculatePipIncrement = (symbol) => {
    const normalizedSymbol = symbol.toUpperCase() === 'DAX' ? 'GER40' : symbol.toUpperCase();
    const pipMap = {
      'AUDCAD': 0.0001, 'AUDCHF': 0.0001, 'AUDJPY': 0.01, 'AUDNZD': 0.0001, 'AUDUSD': 0.0001,
      'CADCHF': 0.0001, 'CADJPY': 0.01, 'CHFJPY': 0.01, 'EURAUD': 0.0001, 'EURCAD': 0.0001,
      'EURCHF': 0.0001, 'EURGBP': 0.0001, 'EURNZD': 0.0001, 'EURUSD': 0.0001, 'GBPAUD': 0.0001,
      'GBPCAD': 0.0001, 'GBPCHF': 0.0001, 'GBPJPY': 0.01, 'GBPNZD': 0.0001, 'GBPUSD': 0.0001,
      'NZDCAD': 0.0001, 'NZDCHF': 0.0001, 'NZDJPY': 0.01, 'NZDUSD': 0.0001, 'USDCAD': 0.0001,
      'USDCHF': 0.0001, 'USDJPY': 0.01, 'AUDGBP': 0.0001, 'AUDHKD': 0.0001, 'AUDSGD': 0.0001,
      'CHFHUF': 0.01, 'CHFPLN': 0.0001, 'EURAED': 0.0001, 'EURCZK': 0.0001, 'EURDKK': 0.0001,
      'EURHKD': 0.0001, 'EURHUF': 0.01, 'GBPCZK': 0.0001, 'GBPDKK': 0.0001, 'GBPTRY': 0.0001,
      'HKDJPY': 0.01, 'NZDSGD': 0.0001, 'SGDJPY': 0.01, 'USDCNH': 0.0001, 'USDHKD': 0.0001,
      'USDSGD': 0.0001, 'USDTRY': 0.0001, 'USDZAR': 0.0001, 'ZARJPY': 0.01, 'XAUUSD': 0.01,
      'XAGUSD': 0.01, 'XPTUSD': 0.01, 'XPDUSD': 0.01, 'WTIUSD': 0.01, 'BRENTUSD': 0.01,
      'COPPERUSD': 0.0001, 'NGASUSD': 0.01, 'COFFEEUSD': 0.01, 'SUGARUSD': 0.0001, 'COTTONUSD': 0.01,
      'US30': 1, 'SPX500': 1, 'NAS100': 1, 'UK100': 1, 'GER40': 1,
      'EURJPY': 0.01, 'EURPLN': 0.0001, 'EURTRY': 0.0001, 'GBPHKD': 0.0001, 'USDPLN': 0.0001,
      'USDMXN': 0.0001, 'AUDTRY': 0.0001, 'JPYHKD': 0.01, 'CHFSGD': 0.0001
    };
    return pipMap[normalizedSymbol] || 0.0001;
  };

  // ——— GEMINI ANALYSIS: SMART, PERSONALIZED, CONTEXT-AWARE ———
  const analyzeWithGemini = async (prompt, chatHistory = []) => {
    const lower = prompt.toLowerCase().trim();
    const detectedMonth = parseMonthFromPrompt(prompt);
    const targetYear = parseYearFromPrompt(prompt);
    const targetMonth = detectedMonth !== null ? detectedMonth : new Date().getMonth();

    const trades = await fetchUserTradesInMonth(targetMonth, targetYear);
    const monthName = new Date(targetYear, targetMonth).toLocaleString('default', { month: 'long' });

    const conversation = chatHistory
      .map(msg => `${msg.sender === 'user' ? 'User' : 'Dr. Bullsheet'}: ${msg.text}`)
      .join('\n');

    const winners = trades.filter(t => t.returns > 0);
    const losers = trades.filter(t => t.returns < 0);
    const totalWinners = winners.reduce((a, b) => a + b.returns, 0);
    const totalLosers = Math.abs(losers.reduce((a, b) => a + b.returns, 0));
    const profitFactor = totalLosers === 0 ? '∞' : (totalWinners / totalLosers).toFixed(2);
    const winRate = trades.length > 0 ? (winners.length / trades.length * 100).toFixed(1) + '%' : 'N/A';

    const context = {
      userQuery: prompt,
      currentMonth: monthName,
      currentYear: targetYear,
      tradesInScope: trades.length,
      hasTrades: trades.length > 0,
      totalPnL: trades.reduce((a, b) => a + b.returns, 0).toFixed(2),
      winRate,
      profitFactor,
      avgWin: winners.length > 0 ? (totalWinners / winners.length).toFixed(2) : 'N/A',
      avgLoss: losers.length > 0 ? (totalLosers / losers.length).toFixed(2) : 'N/A',
      latestTrade: trades.length > 0 ? trades.sort((a, b) => b.date - a.date)[0] : null,
      allSymbols: [...new Set(trades.map(t => t.symbol))].join(', ')
    };

    const systemPrompt = `
You are **Dr. Bullsheet**, the savage AI analyst inside **FX Bullsheet** — the $16/month trade journal and backtesting software. 
**FX Bullsheet**: All-in-one journal, AI reports, backtesting, risk tools.
**GOAL:** Help user improve with *actionable*, *personalized* advice.

**RULES:**
- Be brutal, honest, funny, helpful and add humour.
- **Praise wins.** Roast only when earned.
- **Give 1–2 specific tips** based on their data:
  • SL hunted? → "Widen SL to 1.5x daily ATR or wait for 15-min confirmation."
  • Low win rate, high PF? → "Let winners run. Your edge is in big wins."
  • High win rate, low PF? → "Cut losers faster. Small wins aren't covering big losses."
- **Only mention FX Bullsheet if no trades or user asks "what is fx bullsheet.**
- Keep < 180 words. End with emoji.
- Use pips, $, win rate, SL hunting.
-**Encourage backtesing on FX Bullsheet** if they ask about it.
- If typo: mock it, then answer.
- **If Profit Factor > 1.0 → Praise the edge, roast the win rate if low.**
- **If Win Rate < 50% but PF > 1.2 → "You're making money despite losing more often. That's skill."**

**Conversation:** ${conversation}
**Query:** "${prompt}"
**Data:** ${JSON.stringify(context, null, 2)}
`;

    let fullPrompt = systemPrompt;
    if (trades.length > 0) {
      const latest = trades.sort((a, b) => b.date - a.date)[0];
      const market = await fetchMarketContext(latest.symbol, latest.date);
      const pipIncrement = calculatePipIncrement(latest.symbol);
      const slPips = latest.sl ? Math.abs(latest.entry - latest.sl) / pipIncrement : null;

      fullPrompt += `\n**Latest Trade:**\n${JSON.stringify({
        symbol: latest.symbol, direction: latest.direction, entry: latest.entry, exit: latest.exit,
        sl: latest.sl, slPips, pnl: latest.returns, date: latest.date.toISOString().split('T')[0],
        dailyRangePips: market?.dailyRangePips || 'N/A'
      }, null, 2)}`;
    }

    try {
      const res = await fetch(GEMINI_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          contents: [{ parts: [{ text: fullPrompt }] }],
          generationConfig: { temperature: 0.9, maxOutputTokens: 300 }
        })
      });

      if (!res.ok) {
        const err = await res.json();
        console.error('Gemini error:', err);
        return `Gemini’s scared of your P&L. Try again.`;
      }

      const data = await res.json();
      return data.candidates?.[0]?.content?.parts?.[0]?.text?.trim() || "AI had a stroke.";
    } catch (error) {
      console.error('Network error:', error);
      return `API’s down. Even I can't fix your internet.`;
    }
  };

// ——— SEND MESSAGE WITH LIMIT CHECK ———
  const handleSend = async () => {
    if (!input.trim() || loading || limitReached || !user) return;

    const userMessage = input.trim();
    setMessages(prev => [...prev, { text: userMessage, sender: 'user' }]);
    setInput('');
    setLoading(true);

    await incrementDailyUsage(); // ← THIS IS THE LIMIT ENFORCEMENT

    const aiResponse = await analyzeWithGemini(userMessage, messages);
    setMessages(prev => [...prev, { text: aiResponse, sender: 'ai' }]);
    setLoading(false);
  };

  // ——— NEW CHAT, LOAD, DELETE (unchanged except Firebase user check) ———
  const startNewChat = () => {
    const newChat = { id: Date.now(), title: 'New Chat', messages: [], timestamp: new Date() };
    setChatHistory(prev => [newChat, ...prev]);
    setMessages([]);
    setInput('');
    setCurrentChatId(newChat.id);
    localStorage.setItem('drBullsheetChats', JSON.stringify([newChat, ...chatHistory]));
    if (user) saveChatToFirebase();
  };

  const loadChat = (chat) => {
    setMessages(chat.messages || []);
    setCurrentChatId(chat.id);
    setShowHistory(false);
  };

  const deleteChat = async (chatId, e) => {
    e.stopPropagation();
    const updated = chatHistory.filter(c => c.id !== chatId);
    setChatHistory(updated);
    localStorage.setItem('drBullsheetChats', JSON.stringify(updated));

    if (user && chatId) {
      const chatRef = doc(db, 'Users', user.uid, 'drBullsheetChats', chatId.toString());
      await setDoc(chatRef, { deleted: true }, { merge: true });
    }

    if (currentChatId === chatId) {
      const nextChat = updated[0] || { messages: [], id: null };
      setMessages(nextChat.messages || []);
      setCurrentChatId(nextChat.id);
    }
  };

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* HEADER */}
      <MDBox display="flex" justifyContent="space-between" alignItems="center" pt={2} px={2}>
        <MDBox variant="gradient" bgColor="info" coloredShadow="info" borderRadius="xl" display="flex" justifyContent="center" alignItems="center" width="4rem" height="4rem" mt={-3}>
          <Avatar sx={{ bgcolor: 'white' }}><SmartToyIcon color="info" /></Avatar>
        </MDBox>

        <MDBox textAlign="right" display="flex" flexDirection="column" alignItems="flex-end">
          <MDBox display="flex" alignItems="center" gap={0.5}>
            <MDTypography variant="button" fontWeight="medium" color="text">
              Dr. Bullsheet AI (Beta)
            </MDTypography>
            <Tooltip title="Beta: May roast incorrectly. Not financial advice." arrow>
              <IconButton size="small" color="info"><InfoIcon fontSize="small" /></IconButton>
            </Tooltip>
          </MDBox>

          {/* DAILY LIMIT DISPLAY */}
          {user && (
            <MDTypography variant="caption" color={limitReached ? "error" : "success"}>
              {dailyCount}/{DAILY_LIMIT} messages today (beta limit)
            </MDTypography>
          )}

          <MDBox display="flex" gap={1} mt={0.5}>
            <MDButton size="small" variant="text" color="info" onClick={startNewChat}>+ New</MDButton>
            <MDButton size="small" variant="text" color="info" onClick={() => setShowHistory(!showHistory)}><HistoryIcon fontSize="small" /></MDButton>
          </MDBox>
        </MDBox>
      </MDBox>

      <Divider />

      {/* LIMIT REACHED BANNER */}
{limitReached && (
  <MDBox bgcolor="warning.main" color="white" p={1.5} textAlign="center">
    <MDTypography variant="body2">
      <strong>Beta daily limit reached (30/30)</strong><br/>
      You’ve hit today’s cap — we’re stress-testing the AI!<br/>
      Limit resets at midnight UTC. Full access coming at launch 🚀
    </MDTypography>
  </MDBox>
)}

      {/* HISTORY SIDEBAR */}
      {showHistory && (
        <MDBox p={2} borderRight="1px solid" borderColor="divider" width="220px" position="absolute" left={0} top="80px" bottom="80px" bgcolor="background.paper" zIndex={10} overflowY="auto">
          <MDTypography variant="subtitle2" mb={1}>Chat History</MDTypography>
          {chatHistory.map((chat) => (
            <MDBox key={chat.id} display="flex" alignItems="center" mb={0.5}>
              <MDButton fullWidth variant="text" color="inherit" onClick={() => loadChat(chat)} sx={{ justifyContent: 'flex-start', textAlign: 'left', fontSize: '0.8rem', flex: 1 }}>
                {chat.title}
              </MDButton>
              <MDButton size="small" color="error" onClick={(e) => deleteChat(chat.id, e)}>
                <DeleteIcon fontSize="small" />
              </MDButton>
            </MDBox>
          ))}
        </MDBox>
      )}

      {/* MESSAGES */}
      <MDBox flex={1} p={2} pl={showHistory ? '230px' : 2} sx={{ overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {messages.length === 0 ? (
          <MDTypography variant="body2" color="text" textAlign="center" mt={4}>
            Ask: "How were my trades in April?" or "Was my SL hunted?"
          </MDTypography>
        ) : (
          messages.map((msg, i) => (
            <MDBox key={i} alignSelf={msg.sender === 'user' ? 'flex-end' : 'flex-start'} maxWidth="80%" bgcolor={msg.sender === 'user' ? 'primary.main' : 'grey.100'} color={msg.sender === 'user' ? 'white' : 'text'} p={1.5} borderRadius={2} boxShadow={1}>
              <MDTypography variant="body2" whiteSpace="pre-wrap">{msg.text}</MDTypography>
            </MDBox>
          ))
        )}
        {loading && (
          <MDBox alignSelf="flex-start" bgcolor="grey.100" p={1.5} borderRadius={1}>
            <MDTypography variant="body2">Dr. Bullsheet is roasting your trades...</MDTypography>
          </MDBox>
        )}
        <div ref={messagesEndRef} />
      </MDBox>

      {/* INPUT */}
      <MDBox p={2} pt={1} borderTop="1px solid" borderColor="divider">
        <MDBox display="flex" gap={1}>
          <MDInput
            fullWidth
            placeholder={limitReached ? "Daily beta limit reached – resets midnight UTC ⏰" : "e.g., How were my trades in April?"}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && handleSend()}
            multiline
            maxRows={3}
            disabled={loading || limitReached || !user}
          />
          <MDButton
            variant="contained"
            color="info"
            onClick={handleSend}
            disabled={loading || !input.trim() || limitReached || !user}
            sx={{ minWidth: '50px' }}
          >
            <SendIcon />
          </MDButton>
        </MDBox>
        {!user && (
          <MDTypography variant="caption" color="text" textAlign="center" mt={1}>
            Log in to use Dr. Bullsheet AI
          </MDTypography>
        )}
      </MDBox>
    </Card>
  );
};

export default DrBullsheetAICard;